/**
 * iter-154q — Activate workspace page.
 *
 * Reached via /activate?token=... from either:
 *   1. The welcome email's "Activate workspace" button
 *   2. The marketing site's "Open the App" success-page button
 *
 * Flow:
 *   - Pull `token` from URL
 *   - Call GET /api/auth/activation-info to show user/company info
 *   - User sets password → POST /api/auth/activate
 *   - On success: auto-logged in via cookies → redirect to /onboarding/welcome
 */
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiClient } from '@/api/client';
import { setTokens } from '@/lib/authToken';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Loader2, Sparkles, ShieldCheck, AlertTriangle, Eye, EyeOff } from 'lucide-react';
import { useAuth, formatApiErrorDetail } from '@/contexts/AuthContext';
import { PasswordRules } from '@/components/auth/PasswordRules';
import { PASSWORD_MIN_LENGTH, passwordRuleError } from '@/lib/passwordPolicy';

export default function ActivatePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const token = searchParams.get('token') || '';
  const sessionId = searchParams.get('session_id') || '';

  const [loadingInfo, setLoadingInfo] = useState(true);
  const [info, setInfo] = useState(null);
  const [infoError, setInfoError] = useState(null);
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      // Path A: marketing success-page redirect with ?session_id=...
      // — swap for an activation URL and replace location.
      if (!token && sessionId) {
        try {
          const { data } = await apiClient.post('/auth/exchange-session', { session_id: sessionId });
          if (data?.activate_url) {
            window.location.replace(data.activate_url);
            return;
          }
          setInfoError('Could not generate an activation link for this session.');
          setLoadingInfo(false);
          return;
        } catch (e) {
          const detail = e.response?.data?.detail;
          if (e.response?.status === 425) {
            // Webhook hasn't fired yet — provisioning in flight. Retry once after 2s.
            setTimeout(async () => {
              try {
                const { data } = await apiClient.post('/auth/exchange-session', { session_id: sessionId });
                if (data?.activate_url) window.location.replace(data.activate_url);
                else {
                  setInfoError('Your workspace is still being set up. Check your email shortly for the activation link.');
                  setLoadingInfo(false);
                }
              } catch (e2) {
                setInfoError(e2.response?.data?.detail || 'Could not generate an activation link. Check your email for the welcome message.');
                setLoadingInfo(false);
              }
            }, 2000);
            return;
          }
          setInfoError(detail || 'Could not verify your checkout session.');
          setLoadingInfo(false);
          return;
        }
      }
      // Path B: standard /activate?token=...
      if (!token) {
        setInfoError('Missing activation token. Open the link from your welcome email.');
        setLoadingInfo(false);
        return;
      }
      try {
        const { data } = await apiClient.get('/auth/activation-info', {
          params: { token },
        });
        setInfo(data);
      } catch (e) {
        setInfoError(e.response?.data?.detail || e.message || 'Could not verify activation link.');
      } finally {
        setLoadingInfo(false);
      }
    })();
  }, [token, sessionId]);

  const submit = async (e) => {
    e?.preventDefault?.();
    const pwErr = passwordRuleError(password);
    if (pwErr) {
      toast.error(pwErr);
      return;
    }
    if (password !== passwordConfirm) {
      toast.error("Passwords don't match");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await apiClient.post('/auth/activate', { token, password });
      setTokens(data);
      toast.success(`Welcome, ${data.name || data.email}!`, {
        description: `Your workspace is ready.`,
      });
      // Hydrate AuthContext from the freshly-set cookie session so
      // ProtectedRoute doesn't bounce us back to /login on the next route.
      try { await refreshUser(); } catch (_) { /* non-fatal */ }
      navigate(data.redirect || '/onboarding/welcome');
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message || 'Activation failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="activate-page" className="min-h-screen bg-background text-foreground flex items-center justify-center px-4">
      <Card className="w-full max-w-md bg-card border-border p-8">
        <div className="text-center mb-6">
          <Sparkles className="w-8 h-8 text-[#5C8AC2] mx-auto mb-3" />
          <h1 className="text-2xl font-semibold text-foreground">Activate your workspace</h1>
          {info?.company_name && (
            <p className="text-sm text-muted-foreground mt-1">at <strong>{info.company_name}</strong></p>
          )}
        </div>

        {loadingInfo && (
          <div className="flex flex-col items-center text-muted-foreground py-12" data-testid="activate-loading">
            <Loader2 className="w-6 h-6 animate-spin mb-2" />
            <span className="text-xs">Verifying activation link…</span>
          </div>
        )}

        {!loadingInfo && infoError && (
          <div className="bg-[#3A1212] border border-rose-700/40 rounded p-4 text-sm text-rose-200" data-testid="activate-error">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-rose-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold mb-1">Activation link can't be used</p>
                <p className="text-xs">{infoError}</p>
                <p className="text-xs mt-3 text-muted-foreground">
                  If your link expired or was already used, head to{' '}
                  <a href="/login" className="text-[#5C8AC2] underline">login</a>{' '}
                  and use "Forgot password" to get a new one.
                </p>
              </div>
            </div>
          </div>
        )}

        {!loadingInfo && info?.valid && (
          <form onSubmit={submit} className="space-y-4" data-testid="activate-form">
            <div className="bg-background border border-border rounded p-3 text-xs text-muted-foreground">
              <p>Welcome, <strong className="text-foreground">{info.name || info.email}</strong></p>
              <p className="mt-1">Set a password to finish activating <strong className="text-foreground">{info.email}</strong>.</p>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Password</Label>
              <div className="relative mt-1">
                <Input
                  type={showPwd ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="activate-password"
                  autoComplete="new-password"
                  minLength={PASSWORD_MIN_LENGTH}
                  required
                  className="bg-background border-border text-foreground pr-9"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd(!showPwd)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  tabIndex={-1}
                >
                  {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <PasswordRules password={password} testId="activate-password-rules" />
            </div>

            <div>
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Confirm password</Label>
              <Input
                type={showPwd ? 'text' : 'password'}
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
                data-testid="activate-password-confirm"
                autoComplete="new-password"
                minLength={PASSWORD_MIN_LENGTH}
                required
                className="mt-1 bg-background border-border text-foreground"
              />
            </div>

            <Button
              type="submit"
              disabled={submitting || !password || !passwordConfirm}
              data-testid="activate-submit"
              className="w-full bg-[#5C8AC2] hover:bg-[#7AA3D4] text-white disabled:opacity-50"
            >
              {submitting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Activating…</>
              ) : (
                <><ShieldCheck className="w-4 h-4 mr-2" /> Activate workspace</>
              )}
            </Button>
            <p className="text-[10px] text-muted-foreground text-center">
              By activating, you accept Strata's Terms of Service.
            </p>
          </form>
        )}
      </Card>
    </div>
  );
}
